import config from '../config';

export type ApiRequestOptions<W> = {
  method?: 'GET' | 'PATCH' | 'PUT' | 'POST' | 'DELETE';
  sessionId?: string | null;
  jsonBody?: W;
  uploadFile?: File;
  formBody?: { [key: string]: string };
  responseType?: 'text' | 'blob';
  contentType?: string;
};

export type ApiResponse<R> = {
  status: number;
  headers: object;
  data: R;
};

export class ApiError extends Error {
  requestOptions: ApiRequestOptions<any>;
  response: ApiResponse<any>;
  constructor(
    path: string,
    requestOptions: ApiRequestOptions<any>,
    response: ApiResponse<any>,
  ) {
    super(`${response.status} on ${requestOptions.method ?? 'GET'} ${path}`);
    this.requestOptions = requestOptions;
    this.response = response;
  }
}

/**
 * Given a path + options, resolve to an ApiResponse, or throw an ApiError
 */
export default async function sustainApiRequest<R, W>(
  path: string,
  options: ApiRequestOptions<W>,
): Promise<ApiResponse<R>> {
  const {
    method = 'GET',
    sessionId,
    jsonBody,
    uploadFile,
    formBody,
    responseType = 'text',
  } = options;

  if (jsonBody && uploadFile) {
    throw new Error('Only one of jsonBody and uploadFile should be provided');
  }

  let body: undefined | string | FormData;
  if (jsonBody) body = JSON.stringify(jsonBody);

  if (uploadFile) {
    body = new FormData();
    body.append('file', uploadFile);
    for (const [k, v] of Object.entries(formBody ?? {})) {
      body.append(k, v);
    }
  }

  let contentType;
  if (method === 'PATCH') contentType = 'application/merge-patch+json';
  else if (jsonBody) contentType = 'application/ld+json';

  const headers = {
    Accept: 'application/ld+json',
    ...(sessionId ? { Authorization: sessionId } : {}),
    'Sustain-Platform': 'web',
    'Cache-Control': 'no-cache',
    ...(contentType ? { 'Content-Type': contentType } : {}),
  };

  const fetchResponse = await fetch(`${config.SERVER_ROOT}${path}`, {
    method,
    headers,
    body,
    credentials: 'omit',
  });

  // Debug: request
  if (!fetchResponse.ok) {
    console.log(`>> ${method} ${config.SERVER_ROOT}${path}`);
    for (const [k, v] of Object.entries(headers)) {
      console.log(`>> ${k}: ${v}`);
    }
    if (body) console.log(body instanceof FormData ? '<FormData>' : body);
    console.log('');
  }

  let text: string | null = null;
  let blob: Blob | null = null;
  if (responseType === 'text') {
    text = await fetchResponse.text();
  } else if (responseType === 'blob') {
    blob = await fetchResponse.blob();
  }

  let data: any = null;
  try {
    if (text) {
      data = text ? JSON.parse(text) : null;
    } else if (blob) {
      data = blob;
    }
  } catch (err) {
    throw new Error(`sustainApiRequest: Could not parse JSON: "${text}"`);
  }

  const apiResponse: ApiResponse<R> = {
    data,
    status: fetchResponse.status,
    headers: fetchResponse.headers,
  };

  // Debug: response
  if (!fetchResponse.ok) {
    console.log(`<< ${fetchResponse.status}`);
    for (const [k, v] of (fetchResponse.headers as any).entries()) {
      console.log(`<< ${k}: ${v}`);
    }
    console.log(JSON.stringify(data, undefined, 2));
    console.log('');
    console.log('');
  }

  if (!fetchResponse.ok) {
    throw new ApiError(path, options, apiResponse);
  }

  return apiResponse;
}
